import { Variants } from "framer-motion";

// Base animation timing
export const ANIMATION_DURATION = 0.6;
export const ANIMATION_EASE = "easeOut";
export const STAGGER_DELAY = 0.1;

// Layout IDs organized by component section
export const layoutIds = {
  card: {
    root: "card-root",
    header: "card-header",
    content: "card-content",
    footer: "card-footer",
  },
  player: {
    root: "player-root",
    footer: "player-footer",
  },
  cta: "card-cta",
} as const;

// Animation variants organized by component section
export const variants = {
  // Card section variants - main container and structural sections
  card: {
    // Root card container with blur-in effect
    root: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        transition: {
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,

    // Card header appears first with top-down origin
    header: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
        y: -30,
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        transition: {
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,

    // Card content appears second with slight delay
    content: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
        y: -25,
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        transition: {
          delay: 0.1,
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,

    // Card footer appears last in cascade
    footer: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
        y: -20,
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        transition: {
          delay: 0.2,
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,
  },

  // Player/media section variants
  player: {
    // Player root container
    root: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        transition: {
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,

    // Player footer (controls, identity)
    footer: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
        y: -15,
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        transition: {
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,
  },

  // CTA button variant
  cta: {
    initial: {
      opacity: 0,
      filter: "blur(8px)",
      y: -15,
    },
    animate: {
      opacity: 1,
      filter: "blur(0px)",
      y: 0,
      transition: {
        delay: 0.45,
        duration: 0.25,
        ease: ANIMATION_EASE as any,
      },
    },
  } as Variants,

  // Staggered list container for cascading items
  list: {
    container: {
      initial: {
        opacity: 0,
      },
      animate: {
        opacity: 1,
        transition: {
          staggerChildren: STAGGER_DELAY,
          delayChildren: 0,
        },
      },
    } as Variants,

    // Individual list item with top-down reveal
    item: {
      initial: {
        opacity: 0,
        filter: "blur(8px)",
        y: -15,
      },
      animate: {
        opacity: 1,
        filter: "blur(0px)",
        y: 0,
        transition: {
          duration: ANIMATION_DURATION,
          ease: ANIMATION_EASE as any,
        },
      },
    } as Variants,
  },
} as const;

// Transition configuration for consistent application
export const animationTransition = {
  duration: ANIMATION_DURATION,
  ease: ANIMATION_EASE as any,
};

export const staggeredTransition = {
  staggerChildren: STAGGER_DELAY,
  delayChildren: 0,
};

// Backwards compatibility exports for existing code
export const blurInVariants = variants.card.root;
export const cardSectionVariants = variants.card.content;
export const cardHeaderVariants = variants.card.header;
export const cardContentVariants = variants.card.content;
export const cardFooterVariants = variants.card.footer;
export const ctaButtonVariants = variants.cta;
export const staggerContainerVariants = variants.list.container;
export const staggerItemVariants = variants.list.item;
export const featureCardVariants = variants.list.item;
